import React, { useEffect, useMemo } from 'react'
import { Link, useLocation, useParams } from 'react-router-dom'
import { useTranslation } from '../hooks/useTranslation'
import { useLanguage } from '../contexts/LanguageContext'
import { usePageSeo } from '../hooks/usePageSeo'
import GroupShareActions from '../components/GroupShareActions'
import { getSiteUrl } from '../lib/siteUrl'

const asArray = (value) => (Array.isArray(value) ? value : [])

const Groups = () => {
  const t = useTranslation()
  const { isLoading, language } = useLanguage()
  const location = useLocation()
  const { slug } = useParams()

  const groupItems = asArray(t('groups.items'))
  const activeGroup = useMemo(
    () => (slug ? groupItems.find((g) => g.id === slug) : null),
    [groupItems, slug]
  )

  usePageSeo({
    enabled: !isLoading,
    title: activeGroup
      ? `${activeGroup.title} | VIVA Psychotherapy`
      : `${t('groups.meta.title')} | VIVA Psychotherapy`,
    description: activeGroup
      ? `${activeGroup.tagline}. ${activeGroup.dates}. ${t('groups.shared.time')}.`
      : t('groups.meta.description'),
    pathname: location.pathname,
    imageUrl: activeGroup
      ? `${getSiteUrl()}${activeGroup.image}`
      : undefined,
    imageAlt: activeGroup?.imageAlt,
  })

  useEffect(() => {
    if (!slug || isLoading) return
    const el = document.getElementById(slug)
    if (el) {
      window.requestAnimationFrame(() => {
        el.scrollIntoView({ behavior: 'smooth', block: 'start' })
      })
    }
  }, [slug, isLoading, groupItems])

  if (isLoading) {
    return (
      <div className="min-h-screen bg-primary-offWhite flex items-center justify-center">
        <div className="animate-pulse text-gray-500">Loading…</div>
      </div>
    )
  }

  const whyItems = asArray(t('groups.whyJoin.items'))
  const contactPath = language === 'pt' ? '/pt/contact' : '/contact'

  return (
    <div className="min-h-screen bg-[#f7f3ec] text-primary-darkGray">
      <section className="relative overflow-hidden bg-gradient-to-br from-primary-darkGreen via-[#3d5232] to-[#5a7348] text-white">
        <div
          className="pointer-events-none absolute inset-0 opacity-[0.12]"
          style={{
            backgroundImage:
              'radial-gradient(circle at 20% 20%, rgba(223,176,36,0.35), transparent 45%), radial-gradient(circle at 80% 10%, rgba(255,255,255,0.12), transparent 40%)',
          }}
          aria-hidden
        />
        <div className="relative z-10 mx-auto max-w-5xl px-4 py-16 sm:px-6 sm:py-20 lg:px-8 lg:py-24 text-center">
          <p className="mb-4 text-sm font-semibold uppercase tracking-[0.2em] text-primary-golden">
            VIVA Psychotherapy
          </p>
          <h1 className="mb-5 text-3xl font-bold leading-tight tracking-tight sm:text-4xl lg:text-5xl">
            {t('groups.hero.title')}
          </h1>
          <p className="mx-auto mb-6 max-w-3xl text-lg text-white/95 sm:text-xl">
            {t('groups.hero.subtitle')}
          </p>
          <p className="mx-auto max-w-3xl text-base leading-relaxed text-white/85 sm:text-lg">
            {t('groups.hero.intro')}
          </p>
        </div>
      </section>

      <article className="mx-auto max-w-7xl px-4 py-14 sm:px-6 lg:px-8 lg:py-20 space-y-16 lg:space-y-20">
        <section aria-labelledby="groups-why-heading">
          <h2
            id="groups-why-heading"
            className="mb-4 text-2xl font-bold text-primary-darkGreen sm:text-3xl"
          >
            {t('groups.whyJoin.title')}
          </h2>
          <p className="mb-8 max-w-3xl text-lg leading-relaxed text-gray-700">
            {t('groups.whyJoin.intro')}
          </p>
          <ul className="grid gap-5 md:grid-cols-3">
            {whyItems.map((item) => (
              <li
                key={item.title}
                className="rounded-2xl border border-primary-darkGreen/10 bg-white/80 p-6 shadow-sm backdrop-blur-sm"
              >
                <h3 className="mb-3 text-lg font-bold text-primary-darkGreen">{item.title}</h3>
                <p className="text-sm leading-relaxed text-gray-700 sm:text-base">{item.body}</p>
              </li>
            ))}
          </ul>
        </section>

        <section aria-labelledby="groups-offerings-heading" className="text-center">
          <h2
            id="groups-offerings-heading"
            className="mb-4 text-2xl font-bold text-primary-darkGreen sm:text-3xl"
          >
            {t('groups.offerings.title')}
          </h2>
          <p className="mx-auto mb-4 max-w-3xl text-lg text-gray-700">
            {t('groups.offerings.body')}
          </p>
          <p className="mx-auto max-w-3xl rounded-xl border border-primary-golden/40 bg-[#fff8e8] px-5 py-4 text-sm font-medium text-primary-darkGray sm:text-base">
            {t('groups.offerings.notice')}
          </p>
        </section>

        <section aria-label={t('groups.offerings.title')} className="space-y-14">
          {groupItems.map((group, index) => {
            const topics = asArray(group.topics)
            const takeaways = asArray(group.takeaways)
            const sessions = asArray(group.sessions)
            const imageFirst = index % 2 === 0
            const isHighlighted = slug === group.id

            return (
              <article
                key={group.id}
                id={group.id}
                className={`scroll-mt-28 rounded-3xl border bg-white shadow-md shadow-primary-darkGreen/5 ${
                  isHighlighted
                    ? 'border-primary-golden ring-2 ring-primary-golden/40'
                    : 'border-primary-darkGreen/10'
                }`}
              >
                <div className="grid items-stretch lg:grid-cols-[minmax(22rem,1fr)_minmax(0,1.05fr)]">
                  <div
                    className={`flex min-h-[28rem] items-stretch justify-center overflow-hidden rounded-t-3xl bg-[#efe8dc] p-3 sm:p-4 lg:min-h-0 lg:rounded-t-none lg:p-5 ${
                      imageFirst
                        ? 'lg:rounded-l-3xl lg:rounded-tr-none'
                        : 'lg:order-2 lg:rounded-r-3xl lg:rounded-tl-none'
                    }`}
                  >
                    <figure className="flex h-full w-full items-center justify-center">
                      <img
                        src={group.image}
                        alt={group.imageAlt}
                        className="max-h-full max-w-full h-full w-auto object-contain"
                        width={1080}
                        height={1400}
                        loading={index === 0 ? 'eager' : 'lazy'}
                        decoding="async"
                      />
                    </figure>
                  </div>

                  <div
                    className={`flex flex-col gap-6 p-6 sm:p-8 lg:p-10 ${
                      imageFirst ? '' : 'lg:order-1'
                    }`}
                  >
                    <header>
                      <div className="mb-2 flex items-start justify-between gap-3">
                        <p className="text-xs font-semibold uppercase tracking-[0.16em] text-primary-mediumBrown">
                          {group.language}
                        </p>
                        <GroupShareActions group={group} />
                      </div>
                      <h3 className="text-2xl font-bold leading-tight text-primary-darkGreen sm:text-3xl">
                        {group.title}
                      </h3>
                      <p className="mt-2 text-base font-medium italic text-primary-mediumBrown sm:text-lg">
                        {group.tagline}
                      </p>
                      <p className="mt-4 text-base leading-relaxed text-gray-700">{group.description}</p>
                    </header>

                    <dl className="grid gap-3 rounded-2xl bg-[#f7f3ec] p-4 text-sm sm:grid-cols-2 sm:p-5">
                      <div>
                        <dt className="font-semibold text-primary-darkGreen">
                          {t('groups.shared.audienceLabel')}
                        </dt>
                        <dd className="mt-1 text-gray-700">{group.audience}</dd>
                      </div>
                      <div>
                        <dt className="font-semibold text-primary-darkGreen">
                          {t('groups.shared.formatLabel')}
                        </dt>
                        <dd className="mt-1 text-gray-700">{t('groups.shared.format')}</dd>
                      </div>
                      <div>
                        <dt className="font-semibold text-primary-darkGreen">
                          {t('groups.shared.timeLabel')}
                        </dt>
                        <dd className="mt-1 text-gray-700">{t('groups.shared.time')}</dd>
                      </div>
                      <div>
                        <dt className="font-semibold text-primary-darkGreen">
                          {t('groups.shared.investmentLabel')}
                        </dt>
                        <dd className="mt-1 text-gray-700">{t('groups.shared.investment')}</dd>
                      </div>
                      <div>
                        <dt className="font-semibold text-primary-darkGreen">
                          {t('groups.shared.languageLabel')}
                        </dt>
                        <dd className="mt-1 text-gray-700">{group.language}</dd>
                      </div>
                    </dl>

                    {sessions.length > 0 ? (
                      <div>
                        <h4 className="mb-3 text-sm font-bold uppercase tracking-wide text-primary-darkGreen">
                          {t('groups.shared.timelineHeading')}
                        </h4>
                        <ol className="relative space-y-0 border-l-2 border-primary-golden/70 pl-5">
                          {sessions.map((session, sessionIndex) => (
                            <li key={session.iso} className="relative pb-5 last:pb-0">
                              <span
                                className="absolute -left-[1.4rem] top-1.5 h-3 w-3 rounded-full border-2 border-primary-golden bg-white"
                                aria-hidden
                              />
                              <p className="text-xs font-semibold uppercase tracking-wide text-primary-mediumBrown">
                                {t('groups.shared.sessionLabel')} {sessionIndex + 1}
                              </p>
                              <time
                                dateTime={session.iso}
                                className="mt-0.5 block text-base font-semibold text-primary-darkGreen"
                              >
                                {session.label}
                              </time>
                              <p className="text-sm text-gray-600">{t('groups.shared.time')}</p>
                            </li>
                          ))}
                        </ol>
                      </div>
                    ) : null}

                    {topics.length > 0 ? (
                      <div>
                        <h4 className="mb-2 text-sm font-bold uppercase tracking-wide text-primary-darkGreen">
                          {t('groups.shared.topicsHeading')}
                        </h4>
                        <ul className="space-y-2">
                          {topics.map((topic) => (
                            <li key={topic} className="flex gap-2 text-sm text-gray-700 sm:text-base">
                              <span className="mt-2 h-1.5 w-1.5 shrink-0 rounded-full bg-primary-golden" aria-hidden />
                              <span>{topic}</span>
                            </li>
                          ))}
                        </ul>
                      </div>
                    ) : null}

                    {takeaways.length > 0 && !(takeaways.length === 1 && takeaways[0] === group.slogan) ? (
                      <div>
                        <h4 className="mb-2 text-sm font-bold uppercase tracking-wide text-primary-darkGreen">
                          {t('groups.shared.takeawaysHeading')}
                        </h4>
                        <ul className="space-y-2">
                          {takeaways.map((item) => (
                            <li key={item} className="flex gap-2 text-sm text-gray-700 sm:text-base">
                              <span className="mt-2 h-1.5 w-1.5 shrink-0 rounded-full bg-primary-darkGreen" aria-hidden />
                              <span>{item}</span>
                            </li>
                          ))}
                        </ul>
                      </div>
                    ) : null}

                    {group.slogan ? (
                      <p className="border-l-4 border-primary-golden pl-4 text-base font-semibold text-primary-darkGreen">
                        {group.slogan}
                      </p>
                    ) : null}

                    {group.screeningNote ? (
                      <p className="text-sm leading-relaxed text-gray-600">{group.screeningNote}</p>
                    ) : null}

                    <div className="mt-auto pt-2">
                      <a
                        href={group.registerUrl}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="inline-flex min-h-12 w-fit items-center justify-center rounded-lg bg-primary-darkGreen px-5 py-3 text-sm font-semibold text-white transition hover:bg-primary-darkGreen/90 focus:outline-none focus-visible:ring-2 focus-visible:ring-primary-golden focus-visible:ring-offset-2"
                      >
                        {group.registerCta || t('groups.shared.register')}
                      </a>
                    </div>
                  </div>
                </div>
              </article>
            )
          })}
        </section>

        <section className="rounded-3xl bg-primary-darkGreen px-6 py-10 text-center text-white sm:px-10">
          <h2 className="mb-3 text-2xl font-bold sm:text-3xl">{t('groups.contact.title')}</h2>
          <p className="mx-auto mb-6 max-w-2xl text-white/90">{t('groups.contact.body')}</p>
          <div className="flex flex-wrap items-center justify-center gap-3">
            <a
              href={t('groups.contact.phoneHref')}
              className="inline-flex min-h-12 items-center justify-center rounded-lg bg-primary-golden px-5 py-3 text-sm font-semibold text-primary-darkGray transition hover:bg-[#e8c04a] focus:outline-none focus-visible:ring-2 focus-visible:ring-white focus-visible:ring-offset-2 focus-visible:ring-offset-primary-darkGreen"
            >
              {t('groups.contact.phoneLabel')}
            </a>
            <Link
              to={contactPath}
              className="inline-flex min-h-12 items-center justify-center rounded-lg border border-white/40 bg-white/10 px-5 py-3 text-sm font-semibold text-white transition hover:bg-white/20 focus:outline-none focus-visible:ring-2 focus-visible:ring-white"
            >
              {t('common.nav.contact')}
            </Link>
          </div>
        </section>
      </article>
    </div>
  )
}

export default Groups
